import type { RiskLedgerRecord } from './domain.js';

export function riskAssessmentPeriod(row: RiskLedgerRecord) {
  if ('recordKind' in row) return row.sourceAssessmentPeriod;
  if (row.assessmentPeriod.startsOn && row.assessmentPeriod.endsOn) return row.assessmentPeriod;
  const match = row.migrationContext?.sourceRound.match(/^(\d{4})[-년\s]*(상반기|하반기)$/u);
  return match ? { startsOn: `${match[1]}-${match[2] === '상반기' ? '01-01' : '07-01'}`,
    endsOn: `${match[1]}-${match[2] === '상반기' ? '06-30' : '12-31'}` } : null;
}

/** Count assessment items, never document totals or planned risk scores as completed actions. */
export function riskAnnualItemMetrics(records: readonly RiskLedgerRecord[], year: string,
  asOfDay = new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Seoul' }), readComplete = true) {
  const rows = records.filter(row => {
    const period = riskAssessmentPeriod(row);
    return period && period.startsOn <= `${year}-12-31` && period.endsOn >= `${year}-01-01`;
  });
  const sourceIncomplete = !readComplete || records.some(row => !riskAssessmentPeriod(row)) || rows.some(row =>
    !('recordKind' in row) && (row.migrationContext?.heldItemCount ?? 0) > 0);
  let annualItemCount = 0, annualReductionPlannedCount = 0, annualActionResultCount = 0;
  let resultUnknown = false;
  for (const row of rows) {
    for (const item of row.items) {
      annualItemCount++;
      const historical = 'recordKind' in item;
      const hasMeasure = historical ? Boolean(item.sourceMeasureText?.trim()) : item.additionalMeasures.length > 0;
      if (!hasMeasure || item.residualRisk.score >= item.initialRisk.score) continue;
      annualReductionPlannedCount++;
      if (historical) {
        // A preserved source completion date is evidence only for this year's assessed action.
        const completedOn = item.sourceCompletionDate?.parsedOn;
        const period = riskAssessmentPeriod(row)!;
        if (completedOn?.startsWith(`${year}-`) && completedOn >= period.startsOn && completedOn <= asOfDay)
          annualActionResultCount++;
        else resultUnknown = true;
      } else if (!('recordKind' in row)) {
        const allResults = item.additionalMeasures.every(measure => {
          const matches = row.implementation.filter(result =>
            result.itemId === item.itemId && result.measureId === measure.measureId);
          if (matches.length > 1) resultUnknown = true;
          const result = matches.length === 1 ? matches[0] : undefined;
          return result && ['in-progress', 'verification-requested', 'verified'].includes(result.status) &&
            result.evidence.some(evidence => evidence.serverCommitted && Boolean(evidence.mediaManifestId) &&
              /^[a-f0-9]{64}$/iu.test(evidence.contentHash));
        });
        if (allResults) annualActionResultCount++;
      }
    }
  }
  return { annualItemCount, annualReductionPlannedCount, annualActionResultCount,
    annualItemCountComplete: !sourceIncomplete,
    annualReductionPlannedComplete: !sourceIncomplete,
    annualActionResultComplete: !sourceIncomplete && !resultUnknown };
}
